import { AnimatePresence, motion } from 'framer-motion'
import {
  Facebook,
  Instagram,
  Link as LinkIcon,
  Linkedin,
  Menu,
  Music2,
  ShoppingBag,
  X,
} from 'lucide-react'
import { createPortal } from 'react-dom'
import { NavLink, useLocation } from 'react-router-dom'

import logoProdamac from '@/assets/logo-prodamac.webp'
import { LocaleSwitcher } from '@/app/components/LocaleSwitcher'
import { Container } from '@/components/ui/Container'
import { getLocalizedPath } from '@/content/i18n'
import { navItems, siteContent } from '@/content/siteContent'
import { useMotionPreference } from '@/hooks/useMotionPreference'
import { DURATION, EASE } from '@/lib/motion'
import { cn, getLocalizedValue } from '@/lib/utils'
import { useUiStore } from '@/store/useUiStore'
import { usePublicSiteBundle } from '@/hooks/usePublicSiteBundle'
import type { Direction, Locale } from '@/types/site'


const MOBILE_SOCIAL_ICONS: Record<string, React.ComponentType<{ className?: string }>> = {
  instagram: Instagram,
  tiktok: Music2,
  facebook: Facebook,
  linkedin: Linkedin,
  linktree: LinkIcon,
}

type SiteHeaderProps = {
  locale: Locale
  direction: Direction
}

export function SiteHeader({ locale, direction }: SiteHeaderProps) {
  const reduceMotion = useMotionPreference()
  const location = useLocation()
  const isMenuOpen = useUiStore((state) => state.isMenuOpen)
  const isNavbarPinned = useUiStore((state) => state.isNavbarPinned)
  const toggleMenu = useUiStore((state) => state.toggleMenu)
  const closeMenu = useUiStore((state) => state.closeMenu)

  const siteBundle = usePublicSiteBundle()
  const storeUrl = (() => {
    const int = siteBundle.data?.site_config?.integrations
    if (int && typeof int === 'object') {
      const c = (int as Record<string, unknown>).contact
      if (c && typeof c === 'object') {
        const url = (c as Record<string, unknown>).store_url
        if (typeof url === 'string' && url.trim()) return url.trim()
      }
    }
    return null
  })()

  const isHomeIndex = location.pathname === `/${locale}` || location.pathname === `/${locale}/`
  const isInHero = isHomeIndex && !isNavbarPinned

  return (
    <motion.header
      dir={direction}
      className={cn(
        'fixed inset-x-0 top-0 z-50 border-b transition duration-300',
        isInHero
          ? 'border-transparent bg-transparent'
          : isNavbarPinned
            ? 'border-slate-200 bg-white shadow-card'
            : 'border-transparent bg-white/70 backdrop-blur',
      )}
      initial={reduceMotion ? false : { opacity: 0, y: -10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: DURATION.base, ease: EASE }}
    >
      <Container className={cn('relative flex h-24 items-center gap-4 lg:gap-8', isInHero && 'bg-transparent')}>
        <NavLink
          to={getLocalizedPath(locale)}
          className="relative z-20 flex shrink-0 items-center gap-4"
          onClick={closeMenu}
          aria-label={getLocalizedValue(siteContent.company.legalName, locale)}
        >
          <img src={logoProdamac} alt="Pro Damac General Trading" className="h-20 w-auto max-w-[300px] object-contain" />
        </NavLink>

        {/* Centered in header, same vertical line as h-12 toolbar (locale + store) */}
        <nav className="absolute left-1/2 top-1/2 z-10 hidden -translate-x-1/2 -translate-y-1/2 items-center gap-4 xl:gap-6 lg:flex">
          {navItems.map((item) => {
            const inactiveCls = isInHero
              ? 'border-transparent text-white hover:border-white/50'
              : 'border-transparent text-darkGray hover:text-brand'
            const activeCls = isInHero ? 'border-white text-white' : 'border-brand text-brand'
            const baseCls =
              'inline-flex h-12 items-center whitespace-nowrap border-b-2 text-sm font-bold uppercase leading-none tracking-wide transition xl:text-base'
            if (item.hash) {
              return (
                <a
                  key={item.key}
                  href={`${getLocalizedPath(locale, item.slug)}#${item.hash}`}
                  className={cn(baseCls, inactiveCls)}
                >
                  {getLocalizedValue(item.label, locale)}
                </a>
              )
            }
            return (
              <NavLink
                key={item.key}
                to={getLocalizedPath(locale, item.slug)}
                end={item.slug === ''}
                className={({ isActive }) => cn(baseCls, isActive ? activeCls : inactiveCls)}
              >
                {getLocalizedValue(item.label, locale)}
              </NavLink>
            )
          })}
        </nav>

        <div
          className={cn(
            'relative z-20 ms-auto hidden shrink-0 items-center gap-3 lg:flex',
            direction === 'rtl' && 'flex-row-reverse',
          )}
        >
          <LocaleSwitcher locale={locale} direction={direction} variant={isInHero ? 'light' : 'default'} />
          {storeUrl && (
            <a
              href={storeUrl}
              target="_blank"
              rel="noopener noreferrer"
              className={cn(
                'relative inline-flex h-12 w-12 shrink-0 items-center justify-center rounded-xl border transition',
                isInHero
                  ? 'border-white/60 text-white hover:bg-white/10'
                  : 'border-slate-200 text-slate-700 hover:border-slate-300 hover:bg-slate-50',
              )}
              aria-label={locale === 'ar' ? 'متجر برو دماك' : 'Pro Damac online store'}
            >
              <ShoppingBag className="h-6 w-6" strokeWidth={1.5} />
            </a>
          )}
        </div>

        <button
          type="button"
          className={cn(
            'ms-auto inline-flex min-h-[44px] min-w-[44px] flex-shrink-0 items-center justify-center rounded-lg border lg:hidden',
            isInHero ? 'border-white/40 bg-white/10 text-white hover:bg-white/20' : 'border-slate-300 bg-white text-slate-900 shadow-sm hover:bg-slate-50',
          )}
          onClick={toggleMenu}
          aria-label={isMenuOpen ? 'Close menu' : 'Open menu'}
          aria-expanded={isMenuOpen}
        >
          {isMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </Container>

      {typeof document !== 'undefined' &&
        createPortal(
          <AnimatePresence>
            {isMenuOpen && (
              <>
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.25 }}
                  className="fixed inset-0 z-[9998] bg-slate-950/45 backdrop-blur-[2px] lg:hidden"
                  onClick={closeMenu}
                  aria-hidden
                />
                <motion.div
                  initial={{ x: direction === 'rtl' ? '-100%' : '100%' }}
                  animate={{ x: 0 }}
                  exit={{ x: direction === 'rtl' ? '-100%' : '100%' }}
                  transition={{ type: 'tween', duration: 0.32, ease: [0.22, 1, 0.36, 1] }}
                  className={cn(
                    'fixed top-0 z-[9999] flex h-[100dvh] w-full max-w-[min(100%,380px)] flex-col bg-white shadow-[0_0_40px_-12px_rgba(15,23,42,0.25)] lg:hidden',
                    direction === 'rtl' ? 'left-0 border-r border-slate-200' : 'right-0 border-l border-slate-200',
                  )}
                  dir={direction}
                  onClick={(e) => e.stopPropagation()}
                >
                  <div className="flex shrink-0 items-center justify-between gap-4 border-b border-slate-200 px-5 py-4">
                    <NavLink
                      to={getLocalizedPath(locale)}
                      onClick={closeMenu}
                      className="flex min-w-0 items-center"
                      aria-label={getLocalizedValue(siteContent.company.legalName, locale)}
                    >
                      <img src={logoProdamac} alt="" className="h-14 w-auto max-w-[220px] object-contain" />
                    </NavLink>
                    <button
                      type="button"
                      onClick={closeMenu}
                      className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-slate-900 text-white transition hover:bg-slate-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400 focus-visible:ring-offset-2"
                      aria-label={locale === 'ar' ? 'إغلاق القائمة' : 'Close menu'}
                    >
                      <X className="h-5 w-5" strokeWidth={2.25} />
                    </button>
                  </div>

                  <div className="flex min-h-0 flex-1 flex-col overflow-y-auto overscroll-contain">
                    <div className="flex flex-wrap items-center gap-3 border-b border-slate-100 px-5 py-4">
                      <LocaleSwitcher locale={locale} direction={direction} />
                      {storeUrl && (
                        <a
                          href={storeUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="relative inline-flex h-12 w-12 shrink-0 items-center justify-center rounded-xl border border-slate-200 text-slate-800 transition hover:border-slate-300 hover:bg-slate-50"
                          aria-label={locale === 'ar' ? 'متجر برو دماك' : 'Pro Damac online store'}
                          onClick={closeMenu}
                        >
                          <ShoppingBag className="h-6 w-6" strokeWidth={1.5} />
                        </a>
                      )}
                    </div>

                    <nav className="px-2 pb-4 pt-2" aria-label={locale === 'ar' ? 'التنقل' : 'Main navigation'}>
                      {navItems.map((item, index) => {
                        const baseCls = cn(
                          'flex min-h-[52px] items-center px-4 py-3 text-[0.95rem] font-bold uppercase tracking-[0.12em] transition',
                          index > 0 && 'border-t border-slate-100',
                        )
                        if (item.hash) {
                          return (
                            <a
                              key={item.key}
                              href={`${getLocalizedPath(locale, item.slug)}#${item.hash}`}
                              onClick={closeMenu}
                              className={cn(baseCls, 'text-slate-800 hover:bg-slate-50 hover:text-slate-950')}
                            >
                              {getLocalizedValue(item.label, locale)}
                            </a>
                          )
                        }
                        return (
                          <NavLink
                            key={item.key}
                            to={getLocalizedPath(locale, item.slug)}
                            end={item.slug === ''}
                            onClick={closeMenu}
                            className={({ isActive }) =>
                              cn(
                                baseCls,
                                isActive
                                  ? 'bg-brand/5 text-brand'
                                  : 'text-slate-800 hover:bg-slate-50 hover:text-slate-950',
                              )
                            }
                          >
                            {getLocalizedValue(item.label, locale)}
                          </NavLink>
                        )
                      })}
                    </nav>
                  </div>

                  <div className="shrink-0 border-t border-slate-200 bg-slate-50 px-5 py-6">
                    <p className="mb-4 text-center text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-500">
                      {locale === 'ar' ? 'تابعنا' : 'Follow us'}
                    </p>
                    <div className="flex flex-wrap items-center justify-center gap-3 sm:gap-4">
                      {siteContent.footer.social.map((item) => {
                        const Icon = MOBILE_SOCIAL_ICONS[item.id] ?? LinkIcon
                        const label = getLocalizedValue(item.label, locale)
                        return (
                          <a
                            key={item.id}
                            href={item.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            aria-label={label}
                            className="flex h-12 w-12 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-600 shadow-sm transition hover:border-brand/30 hover:bg-white hover:text-brand hover:shadow-md"
                          >
                            <Icon className="h-5 w-5" aria-hidden />
                          </a>
                        )
                      })}
                    </div>
                  </div>
                </motion.div>
              </>
            )}
          </AnimatePresence>,
          document.body,
        )}
    </motion.header>
  )
}
